import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import {
  Box,
  Text,
  useToast,
  VStack,
  Spinner,
  Button,
  Container,
  Center,
  Heading,
  useBreakpointValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  HStack,
  Badge,
  Divider,
  useDisclosure,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  CardHeader,
  Input
} from "@chakra-ui/react";
import moment from "moment";

// Color theme constants - matching the existing theme
const COLORS = {
  isabelline: "#F4EEEA",    // Background, card backgrounds
  maastrichtBlue: "#0E1735", // Headings, important text
  shadowBlue: "#7481AF",    // Secondary elements, borders, labels
  mandarin: "#F17946",      // CTAs, highlights, errors
  shinyShamrock: "#56A27F"  // Success states, secondary actions
};

export const SMECheck = () => {
  const { registrationID } = useParams();
  const [registrationData, setRegistrationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  
  // For confirmation dialogs
  const { isOpen: isSMEDialogOpen, onOpen: onSMEDialogOpen, onClose: onSMEDialogClose } = useDisclosure();
  const { isOpen: isNotSMEDialogOpen, onOpen: onNotSMEDialogOpen, onClose: onNotSMEDialogClose } = useDisclosure();
  const cancelRef = React.useRef();
  
  // Responsive values
  const headingSize = useBreakpointValue({ base: "lg", md: "xl" });
  const contentSize = useBreakpointValue({ base: "md", md: "lg" });
  const containerMaxW = useBreakpointValue({ base: "95%", md: "container.md" });

  const [newRegistrationID, setNewRegistrationID] = useState('');

  useEffect(() => {
    fetchRegistration();
  }, [registrationID]);

  const fetchRegistration = async () => {
    setLoading(true);
    try {
      // First, fetch the basic registration details
      const { data: regData, error: regError } = await supabase
        .from("registration")
        .select(`
          id, 
          participant_name, 
          participant_email, 
          registration_type,
          nationality,
          sponsor_uen,
          is_sme,
          course_run_id
        `)
        .eq("id", registrationID)
        .single();

      if (regError) throw regError;

      // Proceed to fetch course details regardless of registration type
      const { data: courseData, error: courseError } = await supabase
        .from("full_registration_details_with_enrollment")
        .select(`
          name,
          start_date,
          end_date,
          course_type_code
        `)
        .eq("id", registrationID)
        .single();

      if (courseError) {
        console.error("Error fetching course details:", courseError);
        // We'll still proceed with registration data even if course details are missing
      }

      // Combine the data
      setRegistrationData({
        ...regData,
        course_run_details: courseData || {
          name: "Course details unavailable",
          start_date: null,
          end_date: null,
          course_type_code: ""
        }
      });
    } catch (error) {
      console.error("Error fetching registration:", error);
      toast({
        title: "Error",
        description: "Failed to load registration details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSMEStatus = async (isSME) => {
    setUpdating(true);
    try {
      // Update only the is_sme field in the registration table
      const { error } = await supabase
        .from("registration")
        .update({ is_sme: isSME })
        .eq("id", registrationID);

      if (error) throw error;

      // If marking as SME, send details to the specified endpoint
      if (isSME && registrationData) {
        try {
          const dataToSend = {
            registration_id: registrationData.id,
            participant_name: registrationData.participant_name,
            participant_email: registrationData.participant_email,
            registration_type: registrationData.registration_type,
            course_run_id: registrationData.course_run_id,
            name: registrationData.course_run_details.name,
            start_date: registrationData.course_run_details.start_date,
            end_date: registrationData.course_run_details.end_date,
            course_type_code: registrationData.course_run_details.course_type_code
          };

          const response = await fetch('https://eod7rcewg7p8qqb.m.pipedream.net', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend),
          });

          if (!response.ok) {
            console.error('Failed to send SME notification:', response.statusText);
          } else {
            console.log('SME notification sent successfully');
          }
        } catch (sendError) {
          console.error('Error sending SME notification:', sendError);
          // We don't throw this error as we still want to update the UI even if notification fails
        }
      }

      toast({
        title: "Status Updated",
        description: `Registration has been marked as ${isSME ? "SME" : "non-SME"}.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      // Refetch to show updated data
      fetchRegistration();
    } catch (error) {
      console.error("Error updating SME status:", error);
      toast({
        title: "Update Failed",
        description: "Failed to update SME status. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
      onSMEDialogClose();
      onNotSMEDialogClose();
    }
  };

  return (
    <Box
      minH="100vh"
      w="100%"
      bg={COLORS.isabelline}
      display="flex"
      justifyContent="center"
      alignItems="center"
      py={{ base: 4, md: 8 }}
      px={{ base: 2, md: 0 }}
    >
      {loading ? (
        <Center h="60vh">
          <Spinner
            size="xl"
            thickness="4px"
            speed="0.65s"
            color={COLORS.mandarin}
          />
        </Center>
      ) : (
        <Container maxW={containerMaxW}>
          <Box
            bg="white"
            p={{ base: 4, md: 8 }}
            borderRadius="lg"
            boxShadow="md"
          >
            <VStack spacing={6} align="start">
              <Heading
                size={headingSize}
                color={COLORS.maastrichtBlue}
                borderBottom="2px solid"
                borderColor={COLORS.mandarin}
                pb={2}
                width="100%"
              >
                SME Verification Tool
              </Heading>

              {registrationData ? (
                registrationData.registration_type === "Company-Sponsored" ? (
                  <>
                    {registrationData.is_sme !== null && (
                      <Alert 
                        status={registrationData.is_sme ? "success" : "info"} 
                        borderRadius="md"
                      >
                        <AlertIcon />
                        This registration has already been marked as {registrationData.is_sme ? "SME" : "non-SME"}
                        <br></br>
                        You can update this status below if needed.
                      </Alert>
                    )}

                    <Card width="100%" variant="outline">
                      <CardHeader bg={COLORS.isabelline} borderTopRadius="md">
                        <Heading size="md">Registration Details</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={3} align="start">
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">Registration ID:</Text>
                            <Text>{registrationData.id}</Text>
                          </HStack>
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">Participant Name:</Text>
                            <Text>{registrationData.participant_name}</Text>
                          </HStack>
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">Email:</Text>
                            <Text>{registrationData.participant_email}</Text>
                          </HStack>
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">Registration Type:</Text>
                            <Badge colorScheme="blue">{registrationData.registration_type}</Badge>
                          </HStack>
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">Company UEN:</Text>
                            <Text fontWeight="semibold">{registrationData.sponsor_uen}</Text>
                          </HStack>
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">SME Status:</Text>
                            {registrationData.is_sme === null ? (
                              <Badge colorScheme="gray">Not Verified</Badge>
                            ) : registrationData.is_sme ? (
                              <Badge colorScheme="green">SME</Badge>
                            ) : (
                              <Badge colorScheme="red">Not SME</Badge>
                            )}
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card width="100%" variant="outline">
                      <CardHeader bg={COLORS.isabelline} borderTopRadius="md">
                        <Heading size="md">Course Details</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={3} align="start">
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">Course:</Text>
                            <Text>{registrationData.course_run_details.name}</Text>
                          </HStack>
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">Start Date:</Text>
                            <Text>
                              {registrationData.course_run_details.start_date ? 
                                `${moment(registrationData.course_run_details.start_date).format("DD MMM YYYY")} (${moment(registrationData.course_run_details.start_date).format("ddd")})` : 
                                "Not available"}
                            </Text>
                          </HStack>
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">End Date:</Text>
                            <Text>
                              {registrationData.course_run_details.end_date ? 
                                `${moment(registrationData.course_run_details.end_date).format("DD MMM YYYY")} (${moment(registrationData.course_run_details.end_date).format("ddd")})` : 
                                "Not available"}
                            </Text>
                          </HStack>
                          <HStack width="100%" justifyContent="space-between">
                            <Text fontWeight="bold">Course Code:</Text>
                            <Text>{registrationData.course_run_details.course_type_code}</Text>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card width="100%" variant="filled" bg={COLORS.isabelline}>
                      <CardHeader>
                        <Heading size="md" color={COLORS.maastrichtBlue}>SME Qualification Criteria</Heading>
                      </CardHeader>
                      <CardBody>
                        <Text fontSize={contentSize} mb={4}>
                          To qualify as an SME, the company must meet ALL of the following criteria:
                        </Text>
                        <VStack spacing={2} align="start" pl={4}>
                          <Text>• Have at least 30% local shareholding</Text>
                          <Text>• Annual turnover less than S$100 million</Text>
                          <Text>• OR employment size not more than 200 workers</Text>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Text fontSize={contentSize} color={COLORS.maastrichtBlue} fontWeight="bold" mt={4}>
                      Please verify if this company qualifies as an SME:
                    </Text>

                    <HStack spacing={4} width="100%" justifyContent="center" mt={4}>
                      <Button
                        colorScheme="green"
                        size="lg"
                        onClick={onSMEDialogOpen}
                        isLoading={updating}
                      >
                        Yes, this is an SME
                      </Button>
                      <Button
                        colorScheme="red"
                        size="lg"
                        onClick={onNotSMEDialogOpen}
                        isLoading={updating}
                      >
                        No, not an SME
                      </Button>
                    </HStack>
                  </>
                ) : (
                  <Box textAlign="center" py={10}>
                    <Alert 
                      status="info" 
                      variant="subtle"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      textAlign="center"
                      borderRadius="md"
                      height="230px"
                    >
                      <AlertIcon boxSize="20px" mr={0} />
                      <Heading size="md" mt={4} mb={2}>
                        Not a Company-Sponsored Registration
                      </Heading>
                      <Text maxW="sm">
                        {registrationData.participant_name} is registered as <Badge colorScheme="blue" mx={1}>{registrationData.registration_type}</Badge> 
                        participant, not as Company-Sponsored.
                      </Text>
                      
                    </Alert>
                    <Box mt={6}>
                      <HStack spacing={2} justifyContent="center">
                        <Input 
                          placeholder="Enter a registration ID" 
                          value={newRegistrationID || ''}
                          onChange={(e) => setNewRegistrationID(e.target.value)}
                          size="md"
                          width={{ base: "200px", md: "300px" }}
                          bg="white"
                          borderColor={COLORS.shadowBlue}
                          _hover={{ borderColor: COLORS.maastrichtBlue }}
                          _focus={{ borderColor: COLORS.mandarin, boxShadow: `0 0 0 1px ${COLORS.mandarin}` }}
                        />
                        <Button 
                          colorScheme="blue" 
                          onClick={() => {
                            if (newRegistrationID && newRegistrationID.trim()) {
                              navigate(`/smecheck/${newRegistrationID.trim()}`);
                            }
                          }}
                          isDisabled={!newRegistrationID || !newRegistrationID.trim()}
                        >
                          Check
                        </Button>
                      </HStack>
                    </Box>
                  </Box>
                )
              ) : (
                <Box textAlign="center" py={10}>
                  <Alert 
                    status="warning"
                    variant="subtle"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    borderRadius="md"
                    height="200px"
                  >
                    <AlertIcon boxSize="20px" mr={0} />
                    <Heading size="md" mt={4} mb={2}>
                      Registration Not Found
                    </Heading>
                    <Text maxW="sm">
                      No registration found with ID: <Badge colorScheme="red" mx={1}>{registrationID}</Badge>
                    </Text>
                    <Text mt={4} fontSize="sm" color={COLORS.shadowBlue}>
                      Please check the registration ID and try again.
                    </Text>
                  </Alert>
                  <Box mt={6}>
                    <HStack spacing={2} justifyContent="center">
                      <Input 
                        placeholder="Enter a registration ID" 
                        value={newRegistrationID || ''}
                        onChange={(e) => setNewRegistrationID(e.target.value)}
                        size="md"
                        width={{ base: "200px", md: "300px" }}
                        bg="white"
                        borderColor={COLORS.shadowBlue}
                        _hover={{ borderColor: COLORS.maastrichtBlue }}
                        _focus={{ borderColor: COLORS.mandarin, boxShadow: `0 0 0 1px ${COLORS.mandarin}` }}
                      />
                      <Button 
                        colorScheme="blue" 
                        onClick={() => {
                          if (newRegistrationID && newRegistrationID.trim()) {
                            navigate(`/smecheck/${newRegistrationID.trim()}`);
                          }
                        }}
                        isDisabled={!newRegistrationID || !newRegistrationID.trim()}
                      >
                        Check
                      </Button>
                    </HStack>
                  </Box>
                </Box>
              )}
            </VStack>
          </Box>
        </Container>
      )}

      {/* SME Confirmation Dialog */}
      <AlertDialog
        isOpen={isSMEDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onSMEDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm SME Status
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure this company qualifies as an SME?
              <Box bg={COLORS.isabelline} p={3} borderRadius="md" mt={3}>
                <Text fontWeight="semibold">SME Criteria:</Text>
                <Text>• At least 30% local shareholding</Text>
                <Text>• Annual turnover less than S$100 million</Text>
                <Text>• OR employment size not more than 200 workers</Text>
              </Box>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onSMEDialogClose}>
                Cancel
              </Button>
              <Button colorScheme="green" onClick={() => updateSMEStatus(true)} ml={3}>
                Confirm as SME
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Non-SME Confirmation Dialog */}
      <AlertDialog
        isOpen={isNotSMEDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onNotSMEDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Non-SME Status
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure this company does NOT qualify as an SME?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onNotSMEDialogClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={() => updateSMEStatus(false)} ml={3}>
                Confirm as Non-SME
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SMECheck;
